import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/verify-email`, {
        token
      });
      setStatus('success');
      setMessage(response.data.message);
      toast.success('Email verified successfully!');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed');
      toast.error('Verification failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Building2 className="h-8 w-8 text-orange-500" />
          <h1 className="text-2xl font-bold text-slate-900">OnsiteHand</h1>
        </div>

        {status === 'verifying' && (
          <div data-testid="verifying-state">
            <Loader2 className="h-16 w-16 text-orange-500 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Verifying Email...</h2>
            <p className="text-slate-600">Please wait while we verify your email address.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="success-state">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Email Verified!</h2>
            <p className="text-slate-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="btn-accent w-full"
              data-testid="goto-login-btn"
            >
              Go to Login
            </button>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="error-state">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Verification Failed</h2>
            <p className="text-slate-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="btn-secondary w-full"
              data-testid="goto-login-btn-error"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
